import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as documentApi from '../services/documentService';

export function useDocumentTree(spaceId?: string) {
  return useQuery({
    queryKey: ['documents', 'tree', spaceId],
    queryFn: () => documentApi.fetchDocumentTree(spaceId!),
    enabled: !!spaceId,
  });
}

export function useDocument(spaceId?: string, documentId?: string) {
  return useQuery({
    queryKey: ['documents', spaceId, documentId],
    queryFn: () => documentApi.fetchDocument(spaceId!, documentId!),
    enabled: !!spaceId && !!documentId,
  });
}

export function useCreateDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ spaceId, data }: { spaceId: string; data: Parameters<typeof documentApi.createDocument>[1] }) =>
      documentApi.createDocument(spaceId, data),
    onSuccess: (_data, { spaceId }) => {
      qc.invalidateQueries({ queryKey: ['documents', 'tree', spaceId] });
      // 카드의 문서 카운트(배포/라이브/내 초안) 갱신
      qc.invalidateQueries({ queryKey: ['team-spaces'] });
    },
  });
}

export function useUpdateDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      spaceId,
      documentId,
      data,
    }: {
      spaceId: string;
      documentId: string;
      data: Parameters<typeof documentApi.updateDocument>[2];
    }) => documentApi.updateDocument(spaceId, documentId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents'] }),
  });
}

export function useDeleteDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ spaceId, documentId }: { spaceId: string; documentId: string }) =>
      documentApi.deleteDocument(spaceId, documentId),
    onSuccess: (_data, { spaceId }) => {
      qc.invalidateQueries({ queryKey: ['documents', 'tree', spaceId] });
      qc.invalidateQueries({ queryKey: ['team-spaces'] });
    },
  });
}

export function useDuplicateDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ spaceId, documentId }: { spaceId: string; documentId: string }) =>
      documentApi.duplicateDocument(spaceId, documentId),
    onSuccess: (_data, { spaceId }) => {
      qc.invalidateQueries({ queryKey: ['documents', 'tree', spaceId] });
      qc.invalidateQueries({ queryKey: ['team-spaces'] });
    },
  });
}

export function useToggleDocumentFavorite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ spaceId, documentId }: { spaceId: string; documentId: string }) =>
      documentApi.toggleDocumentFavorite(spaceId, documentId),
    onSuccess: (_data, { spaceId }) => {
      qc.invalidateQueries({ queryKey: ['documents', 'favorites', spaceId] });
      qc.invalidateQueries({ queryKey: ['documents', spaceId] });
    },
  });
}

export function useDocumentFavorites(spaceId?: string) {
  return useQuery({
    queryKey: ['documents', 'favorites', spaceId],
    queryFn: () => documentApi.fetchDocumentFavorites(spaceId!),
    enabled: !!spaceId,
  });
}

export function useUpdateDocumentStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      spaceId,
      documentId,
      status,
    }: {
      spaceId: string;
      documentId: string;
      status: 'DRAFT' | 'PUBLISHED' | 'LIVE';
    }) => documentApi.updateDocumentStatus(spaceId, documentId, status),
    onSuccess: (_data, { spaceId, documentId }) => {
      qc.invalidateQueries({ queryKey: ['documents', spaceId, documentId] });
      qc.invalidateQueries({ queryKey: ['documents', 'tree', spaceId] });
      // DRAFT ↔ PUBLISHED/LIVE 전환 시 카드 카운트도 갱신
      qc.invalidateQueries({ queryKey: ['team-spaces'] });
    },
  });
}
